import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Download, FileText } from 'lucide-react';
import { reportsService } from '../../services/reportsService';
import { useAuth } from '../../context/AuthContext';

const Receivables = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canView = user && ['super_admin', 'finance_manager'].includes(user.role);

  const loadReport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsService.getReceivables();
      setData(res.data || res);
    } catch (err) {
      setError(err.message || 'Unable to load receivables data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) loadReport();
  }, [canView]);

 const handleExport = async () => {
    try {
      await reportsService.exportReceivables();
    } catch (err) {
      alert(err.message || 'Export failed');
    }
  };

  if (!canView) {
    return (
      <div className="space-y-6 pb-10">
        <h1 className="text-xl font-bold">Receivables</h1>
        <div className="rounded-card border border-gray-100 bg-yellow-50 p-6 text-yellow-800">
          You do not have access to this report.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 text-gray-900">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button onClick={() => navigate('/reports')} className="inline-flex items-center gap-2 text-sm text-primary font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to Reports
          </button>
          <h1 className="text-xl font-bold mt-2">Receivables</h1>
          <p className="text-sm text-gray-500">Outstanding and overdue client invoices.</p>
        </div>
        <button onClick={handleExport} className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark">
          <Download className="w-4 h-4" /> Export Excel
        </button>
      </div>

      {error && <div className="rounded-card border border-red-100 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary w-10 h-10" /></div>
      ) : (
        <div className="rounded-card border border-gray-100 bg-white p-6 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Milestone</th>
                <th className="px-4 py-3">Due Date</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Days Overdue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-400">No receivables found.</td>
                </tr>
              ) : (
                data.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900">{item.client_name}</td>
                    <td className="px-4 py-3 text-gray-600">{item.project_name}</td>
                    <td className="px-4 py-3 text-gray-600">{item.milestone_id}</td>
                    <td className="px-4 py-3 text-gray-600">{item.due_date}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{item.amount?.toLocaleString() ?? '0'}</td>
                    <td className="px-4 py-3 text-gray-700">{item.status}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{item.days_overdue}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Receivables;
